import { Tab, Tabs } from '@mui/material';
import { Alert } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import ApplicationsService from 'services/api/ApplicationsService';
import useRequest from 'hooks/useRequest';
import routes from 'store/configs/Routes';
import { getTabKeyByRoute, getTabRouteByKey } from 'util/Route';
import ApplicationReviewsTable from 'components/applications/ApplicationReviewsTable';
import ApplicationReview from 'store/types/ApplicationReview';
import UserApplicationsPageView from 'components/applications/UserApplicationsPageView';
import Spinner from 'components/shared/Spinner';
import NotFoundPage from 'components/layout/NotFoundPage';
import { getFormattedApplicationReviewStatus } from 'util/Application';
import { ConfigContext } from 'components/ConfigGuard';
import { getDefaultTabRoute, getVisibleTabs } from 'util/TabHelper';
import { TabItem } from 'store/types/Tabs';

import commonStyles from 'styles/common.module.scss';

const tabConfig = {
  Applications: { key: 'MyApplications', label: 'My Applications', route: routes.applications } as TabItem,
  ApplicationReviews: { key: 'MyReviews', label: 'My Reviews', route: routes.applicationReviews } as TabItem,
};

const ApplicationsPage: React.FunctionComponent = () => {
  const location = useLocation();
  const history = useHistory();
  const [applications, setApplications] = useState<ApplicationReview[]>([]);
  const { data, loading, error } = useRequest<ApplicationReview[]>(ApplicationsService.getApplicationReviews);
  const reviewsEnabled: boolean = useMemo(() => !loading && !!data && !!data.length, [loading, data]);
  const isReviewsPage: boolean = useMemo(() => location.pathname.includes(routes.applicationReviews), [location]);
  const activeTab: string = useMemo(() => getTabKeyByRoute(location.pathname, tabConfig), [location.pathname]);
  const {
    modulesSettings: {
      applications: { hiddenTabs },
    },
  } = useContext(ConfigContext);

  const visibleTabs = useMemo(() => {
    return getVisibleTabs(tabConfig, hiddenTabs);
  }, [hiddenTabs]);

  useEffect(() => {
    if (data?.length) {
      setApplications(
        data.map((application) => ({ ...application, status: getFormattedApplicationReviewStatus(application.status) }))
      );
    }
  }, [data]);

  useEffect(() => {
    history.replace(getDefaultTabRoute(location.pathname, visibleTabs));
  }, [history, location.pathname, visibleTabs]);

  const handleTabChange = useCallback(
    (_: any, value = tabConfig.Applications.key) => {
      history.replace(getTabRouteByKey(value, tabConfig));
    },
    [history]
  );

  return (
    <>
      <h1 className={commonStyles.pageTitle}>{isReviewsPage ? 'Reviews' : 'Applications'}</h1>
      <Spinner loading={loading} transparent={false}>
        {reviewsEnabled ? (
          <TabContext value={activeTab}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor={'primary'}
              textColor={'primary'}
              variant={'scrollable'}
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={tab.label}
                  className={commonStyles.tab}
                  classes={{ selected: commonStyles.activeTab }}
                />
              ))}
            </Tabs>
            <TabPanel value={tabConfig.Applications.key} className={commonStyles.tabPanel}>
              <UserApplicationsPageView />
            </TabPanel>
            <TabPanel value={tabConfig.ApplicationReviews.key} className={commonStyles.tabPanel}>
              <ApplicationReviewsTable data={applications} />
            </TabPanel>
          </TabContext>
        ) : (
          <>
            {isReviewsPage ? (
              error ? (
                <Alert severity={'error'} className={commonStyles.alert}>
                  {error}
                </Alert>
              ) : (
                <NotFoundPage />
              )
            ) : (
              <UserApplicationsPageView />
            )}
          </>
        )}
      </Spinner>
    </>
  );
};

export default ApplicationsPage;
